// @givmo/design-system — ProviderButton (SaaS layer).
//
// The Google / Microsoft sign-in control every ADMIN-FLOW door renders — the doors that offer the
// `@givmo/auth` ADMIN flow set (the employer portal's chooser + sign-up chooser, the charity door,
// the company console's door, the board module's door; the super-admin scaffold's lone Google button
// and the consumer apps' Apple + Google set are different doors and do not consume this): a SaaS
// `secondary` (outline) `lg` full-width button carrying the REAL provider mark as an inline SVG —
// Google's 4-colour "G", Microsoft's 4-square mark — so a provider control reads as that provider's
// on every such door alike. Promoted here (OM-14 walkthrough W1, 2026-09-03) from two code-identical
// app copies (`apps/admin`, `apps/charity` — only their header comments differed) after the company
// console shipped its door as plain text buttons: the mark is what makes the control recognisable,
// and a door without it read as a lesser product.
// There is deliberately ONE of these now; a third copy is the drift this file exists to stop.
//
// The LABEL is the caller's — "Sign in with …", "Register with …", "Continue with …" — because the
// verb is a fact about the door (one that only signs in, one that only registers, one that
// creates-or-signs-in), not about the provider. So is the TESTID, per the package rule (emitted only
// when given): each door keeps its own e2e-pinned ids rather than inheriting a default from here.
//
// `loading` shows the pending spinner (in place of the mark and label) and disables; `disabled`
// disables without a spinner — a sibling control is the one pending. The marks are inline so they
// survive without an asset pipeline, and `aria-hidden` so the accessible name is exactly the label.
import { Button } from './button';
import type { TestIdProps } from '../lib/testid';

/** The two providers an ADMIN-flow door offers — the same set as `@givmo/auth`'s `AdminProviderId`. */
export type AuthProviderId = 'google' | 'microsoft';

export interface ProviderButtonProps extends TestIdProps {
  provider: AuthProviderId;
  /** The visible label, and therefore the accessible name — the door's own verb. */
  label: string;
  /** Pending: spinner in place of the mark + label, clicks suppressed. */
  loading?: boolean;
  /** Disabled without a spinner (another control on the door is pending). */
  disabled?: boolean;
  onClick: () => void;
}

/** Google "G" mark (official 4-colour), inline so it survives without an asset pipeline. */
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

/** Microsoft 4-square mark (official colours). */
function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden focusable="false">
      <path fill="#F25022" d="M0 0h7.6v7.6H0z" />
      <path fill="#7FBA00" d="M8.4 0H16v7.6H8.4z" />
      <path fill="#00A4EF" d="M0 8.4h7.6V16H0z" />
      <path fill="#FFB900" d="M8.4 8.4H16V16H8.4z" />
    </svg>
  );
}

export function ProviderButton({
  provider,
  label,
  loading = false,
  disabled = false,
  onClick,
  testId,
}: ProviderButtonProps) {
  return (
    <Button
      appearance="saas"
      tone="secondary"
      size="lg"
      fullWidth
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      leadingIcon={provider === 'google' ? <GoogleLogo /> : <MicrosoftLogo />}
      className="justify-center gap-10 font-w600"
      testId={testId}
    >
      {label}
    </Button>
  );
}
